/**
 * V113-7-0 / V113-7 — the workspace's cross-system Monitoring table (D-113-13: system × class × coverage × open
 * monitoring findings × pack validity). One read: GET /api/w/:slug/monitoring/systems
 * (routes/monitoring-summary.ts, any member, at most `limit` systems with `truncated`). Reading sets nothing.
 */
import type { CarriedCounts, MonitoringInProgressAct, NextMonitoringAction, PackValidityStatus, SystemPackState } from '@aigovops/core';
import { req } from './api';

export type { CarriedCounts, NextMonitoringAction, SystemPackState };

/** D-113-31 internal layer (never on a signed surface): the first state-changing act on a New system. */
export interface MonitoringInProgressView {
  /** Display name of the member who acted (never an email or id). */
  by_name: string | null;
  since: string;
  act: MonitoringInProgressAct;
  link: string | null;
}

export interface MonitoringSummaryRow {
  system_id: string;
  name: string;
  system_class: string | null;
  /** `declared` | `inferred` | null. */
  system_class_source: string | null;
  /** score = covered / applicable (0..1); null when nothing is prescribed (unclassed). `covered` here = passing. */
  coverage: { score: number | null; covered: number; applicable: number };
  /** V113-7: the headline — controls passing now. */
  passing: number;
  /** Prescribed controls (after v11.4 N/A exclusions). */
  of: number;
  /** Controls with a working method (passing or not); `covered >= passing`. */
  covered: number;
  unmonitored_components: number;
  /** V113-7: live configuration items linked to the system (any review state). */
  configuration_items_total: number;
  pending_review_components: number;
  out_of_scope_components: number;
  /** v11.4: controls excluded as not applicable. */
  excluded_controls: number;
  open_monitoring_findings: { total: number; by_kind: Record<string, number> };
  /** D-113-30: open monitoring issues (same number as `open_monitoring_findings.total`). */
  open_issues: number;
  /** Pack-validity reason codes that name this system. */
  pack_reasons: string[];
  /** The system's state in the latest signed pack; null before any pack is signed. */
  pack_state: SystemPackState | null;
  /** D-113-29: exceptions this pack carried for the system that are still open; null when none. */
  carried: CarriedCounts | null;
  /** What the signed pack attested for the system ("attested at 4 of 6"); null when not in a pack or a /1 pack. */
  attested_pass: number | null;
  attested_of: number | null;
  /** D-113-31: New since the re-signed pack until one of its methods reports (also signed). */
  new_since_pack: boolean;
  new_since: string | null;
  /** D-113-31 internal: "in progress" (never signed); null when no member has acted yet. */
  in_progress: MonitoringInProgressView | null;
  /** The nearest SLA of an open monitoring finding; null when none has one (no other due date). */
  sla_due: string | null;
  /** The monitoring owner's display name (never an email); null when none is set. */
  owner_name: string | null;
  /** ONE next action (failing > overdue / nearest SLA > coverage gap > set / confirm class); null when nothing is asked. */
  next_action: NextMonitoringAction | null;
}

export interface MonitoringSummaryTiles {
  passing: number;
  of: number;
  covered: number;
  unmonitored_components: number;
  open_issues: number;
  systems_not_in_pack: number;
  /** Not derivable from stored history yet: always null today (render no delta chip). */
  passing_delta_7d: number | null;
  unmonitored_delta: number | null;
}

export interface MonitoringSummary {
  /** Mean over classed systems; null when none. */
  score: number | null;
  classed_systems: number;
  unclassed_systems: number;
  unmonitored_components: number;
  pending_review_components: number;
  /** null before any pack is signed. */
  pack: {
    status: PackValidityStatus; version: number; signed_at: string; reason_count: number;
    carried: CarriedCounts | null; next_at_risk_at: string | null;
  } | null;
  tiles: MonitoringSummaryTiles;
  systems: MonitoringSummaryRow[];
  /** Set when `program` was asked: `applicable: false` = the program does not scope by systems (the list is empty). */
  program: { key: string; applicable: boolean } | null;
  /** True when the workspace has more than `limit` systems (the list is cut in name order). */
  truncated: boolean;
  limit: number;
}

/** `program`: limit to the systems in scope for that program (the Operate card); an unknown key is 400 `unknown_program`. */
/** The nav badge: open monitoring issues, one count query (never the per-system summary). */
export const getMonitoringBadge = (slug: string) =>
  req<{ open_issues: number }>(`/api/w/${encodeURIComponent(slug)}/monitoring/badge`);

export const getMonitoringSummary = (slug: string, opts: { program?: string } = {}) =>
  req<MonitoringSummary>(`/api/w/${encodeURIComponent(slug)}/monitoring/systems${opts.program ? `?program=${encodeURIComponent(opts.program)}` : ''}`);
